import React, { useRef, useState, useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";
import { FilteredFacet } from "@/apiUtils/algoliaUtils/types";
import { getHtmlInnerText } from "@/apiUtils/htmlDataPerserUtils";

interface IProps {
  tab: FilteredFacet;
  lang: string;
  position?: "top" | "bottom" | "left" | "right";
  children: ReactNode;
}

const SiteInfo = ({ tab, lang, position = "top", children }: IProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const hasContent = (content: string) => content && getHtmlInnerText(content)?.length > 0;

  useEffect(() => {
    if (showTooltip && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      let top = rect.top;
      let left = rect.left;

      switch (position) {
        case "top":
          top -= 40;
          left += rect.width / 2;
          break;
        case "bottom":
          top += rect.height + 10;
          left += rect.width / 2;
          break;
        case "left":
          top += rect.height / 2;
          left -= 160;
          break;
        case "right":
          top += rect.height / 2;
          left += rect.width + 10;
          break;
      }

      setCoords({ top, left });
    }
  }, [showTooltip, position]);

  const tooltip =
    hasContent(tab?.siteInfo?.[lang]) && showTooltip && coords
      ? createPortal(
          <div
            className="fixed z-[9999] whitespace-nowrap text-sm text-white transition-opacity duration-200"
            style={{ top: coords.top, left: coords.left, transform: "translate(-50%, 0)" }}
          >
            <div className="relative rounded bg-black px-3 py-2 shadow-lg">
              <div
                className={`absolute h-2 w-2 rotate-45 bg-black ${
                  position === "top"
                    ? "bottom-[-4px] left-1/2 -translate-x-1/2"
                    : position === "bottom"
                      ? "left-1/2 top-[-4px] -translate-x-1/2"
                      : position === "left"
                        ? "right-[-4px] top-1/2 -translate-y-1/2"
                        : "left-[-4px] top-1/2 -translate-y-1/2"
                }`}
              />
              <div dangerouslySetInnerHTML={{ __html: tab.siteInfo[lang] }} />
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {hasContent(tab?.siteInfo?.[lang]) && (
        <div
          ref={wrapperRef}
          className="relative inline-flex"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {children}
          {tooltip}
        </div>
      )}
    </>
  );
};

export default SiteInfo;
